import {
  core_v1_DataSourceSpec,
  dbui_v1_CreateFunctionRequest,
} from '@antimatterhq/asl-api-ts-v1';
import { setPreserveUnreferencedColumns } from './preserveUnreferencedColumns';
import { setJsonExtract } from './jsonExtractions';
import { setTemporaryFields } from './temporaryFields';
import {
  CustomFunctionSchemaValues,
  DataSourceTableSchemaValues,
} from '@/zod/schemas/dataSource/Common';
import { Datasource_FieldSpec } from '@/types/api';
import * as notebookHelper from '@/lib/notebook/notebookHelper';

export type CustomFunction = NonNullable<
  NonNullable<core_v1_DataSourceSpec['silver']>['preTransform']
>['custom'];

export const getCustomFunctionValues = (
  custom: CustomFunction,
): CustomFunctionSchemaValues => {
  const customFunctionOptions = Object.entries(custom?.options ?? []).map(
    ([key, value]) => ({
      key,
      value,
    }),
  );

  return {
    customFunction: custom?.function ?? '',
    customFunctionOptions: customFunctionOptions.length
      ? customFunctionOptions
      : [{ key: '', value: '' }],
  };
};

export const setCustomFunctionRequest = (
  notebook: string,
  name: string,
  values?: DataSourceTableSchemaValues,
  fields?: Datasource_FieldSpec[],
) => {
  let utils: dbui_v1_CreateFunctionRequest['utils'] | undefined = undefined;
  if (values) {
    utils = {
      unreferencedColumns: setPreserveUnreferencedColumns(values),
      jsonExtract: setJsonExtract(values.jsonExtract),
      temporaryFields: setTemporaryFields(values),
    };
  }
  const result: dbui_v1_CreateFunctionRequest = {
    name,
    path: notebook,
    preFilter: values?.filter || undefined,
    postFilter: values?.postFilter || undefined,
    fields,
    utils,
    templateType: 'datasource',
  };

  return result;
};

export const setCustomFunction = (
  values: CustomFunctionSchemaValues,
): CustomFunction => {
  const { customFunction, customFunctionOptions } = values;
  const options = customFunctionOptions.filter(
    ({ key, value }) => key || value,
  );
  return customFunction
    ? {
        function: customFunction,
        options: options.length
          ? options.reduce(
              (acc, { key, value }) => ({
                ...acc,
                [key || '']: value,
              }),
              {},
            )
          : undefined,
      }
    : undefined;
};

export const generateNotebookName = (datasourceName: string): string => {
  return notebookHelper.generateNotebookName(datasourceName, 'datasource');
};

export const generateNotebookFunctionName = (
  datasourceName: string,
  tableName: string,
): string => {
  return notebookHelper.generateNotebookFunctionName(
    `${datasourceName}_${tableName}`,
    'datasource',
  );
};
